import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, HTTPError, patch, post } from "common/helpers/HTTP";
import type {
  CrmExportType,
  CrmIntegrationSettingsItem,
  IntegrationSettings,
  OutreachSourceType,
  Rb2bIntegration,
} from "common/types";

import { INTEGRATIONS_MAP } from "../constants";

async function removeIntegrationRequest(
  workspaceId: string,
  integrationKey: keyof IntegrationSettings,
): Promise<string> {
  return (await del(`workspaces/${workspaceId}/integrations/${integrationKey}`))
    .install_url;
}

async function updateCrmIntegrationRequest(
  workspaceId: string,
  crmExportType: CrmExportType,
  updates: CrmIntegrationSettingsItem,
) {
  await patch(
    `workspaces/${workspaceId}/integrations/${crmExportType}`,
    updates,
  );
}

async function connectOutreachIntegrationRequest(
  workspaceId: string,
  type: OutreachSourceType,
  api_key: string,
  customUrl: string | null,
): Promise<string | undefined> {
  const response = await post(
    `workspaces/${workspaceId}/integrations/${type}`,
    type === "emailbison" ? { api_key, custom_url: customUrl } : { api_key },
  );

  if (type === "emailbison") {
    return response.user_name;
  }

  return response.workspace_name;
}

async function updateEmailBisonIntegrationRequest(
  workspaceId: string,
  customUrl: string | null,
) {
  await patch(`workspaces/${workspaceId}/integrations/emailbison`, {
    custom_url: customUrl,
  });
}

async function connectRb2bRequest(
  workspaceId: string,
  campaignId: string,
): Promise<Rb2bIntegration> {
  const response = await post(
    `workspaces/${workspaceId}/integrations/rb2b/campaigns/${campaignId}`,
  );

  return response;
}

export default function useIntegrationMutations() {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const queryClient = useQueryClient();

  const {
    mutateAsync: updateCrmIntegration,
    isPending: isUpdatingCrmIntegration,
  } = useMutation({
    mutationFn: ({
      updates,
      crmExportType,
    }: {
      updates: CrmIntegrationSettingsItem;
      crmExportType: CrmExportType;
    }) => updateCrmIntegrationRequest(workspaceId, crmExportType, updates),
    onSuccess: (_, { updates, crmExportType }) => {
      queryClient.setQueryData<IntegrationSettings>(
        getQueryKeys(workspaceId).integrationSettingsKey,
        (prevSettings) => ({
          ...prevSettings,
          [crmExportType]: updates,
        }),
      );
    },
    onError: () => toast.error("Failed to update integration"),
  });

  const { mutateAsync: removeIntegration, isPending: isRemovingIntegration } =
    useMutation({
      mutationFn: (integrationKey: keyof IntegrationSettings) =>
        removeIntegrationRequest(workspaceId, integrationKey),
      onSuccess: (installUrl, integrationKey) => {
        queryClient.setQueryData<IntegrationSettings>(
          getQueryKeys(workspaceId).integrationSettingsKey,
          (prevSettings) => ({
            ...prevSettings,
            [integrationKey]:
              integrationKey === "hubspot" || integrationKey === "pipedrive"
                ? { install_url: installUrl }
                : null,
          }),
        );

        toast.success("Integration removed");
      },
      onError: () => toast.error("Failed to remove integration"),
    });

  const {
    mutateAsync: connectOutreachIntegration,
    isPending: isConnectingOutreachIntegration,
  } = useMutation({
    mutationFn: ({
      type,
      apiKey,
      customUrl,
    }: {
      type: OutreachSourceType;
      apiKey: string;
      customUrl: string | null;
    }) =>
      connectOutreachIntegrationRequest(workspaceId, type, apiKey, customUrl),
    onSuccess: (name, { type, apiKey, customUrl }) => {
      let integrationUpdate: Partial<IntegrationSettings>;

      if (type === "instantly") {
        integrationUpdate = {
          instantly: {
            workspace_name: name,
          },
        };
      } else if (type === "emailbison") {
        integrationUpdate = {
          emailbison: {
            user_name: name,
            api_key: apiKey,
            custom_url: customUrl,
          },
        };
      } else {
        integrationUpdate = {
          smartlead: {
            api_key: apiKey,
          },
        };
      }

      queryClient.setQueryData<IntegrationSettings>(
        getQueryKeys(workspaceId).integrationSettingsKey,
        (prevSettings) => ({
          ...prevSettings,
          ...integrationUpdate,
        }),
      );

      toast.success(`Connected with ${INTEGRATIONS_MAP[type].title}`);
    },
    onError: (err) =>
      toast.error(
        err instanceof HTTPError && err.code === 401 && err.serverMessage
          ? err.serverMessage
          : "Failed to connect integration",
      ),
  });

  const {
    mutateAsync: updateEmailBisonIntegration,
    isPending: isUpdatingEmailBisonIntegration,
  } = useMutation({
    mutationFn: (customUrl: string | null) =>
      updateEmailBisonIntegrationRequest(workspaceId, customUrl),
    onSuccess: (_, customUrl) => {
      queryClient.setQueryData<IntegrationSettings>(
        getQueryKeys(workspaceId).integrationSettingsKey,
        (prevSettings) => ({
          ...prevSettings,
          emailbison: {
            ...prevSettings.emailbison,
            custom_url: customUrl,
          },
        }),
      );
    },
    onError: () => toast.error("Failed to update custom domain"),
  });

  const { mutateAsync: connectRb2b, isPending: isConnectingRb2b } = useMutation(
    {
      mutationFn: (campaignId: string) =>
        connectRb2bRequest(workspaceId, campaignId),
      onSuccess: (rb2bIntegration) => {
        queryClient.setQueryData<IntegrationSettings>(
          getQueryKeys(workspaceId).integrationSettingsKey,
          (prevSettings) => ({
            ...prevSettings,
            rb2b: rb2bIntegration,
          }),
        );
      },
      onError: () => toast.error("Failed to connect RB2B"),
    },
  );

  return {
    updateCrmIntegration,
    isUpdatingCrmIntegration,
    removeIntegration,
    isRemovingIntegration,
    connectOutreachIntegration,
    isConnectingOutreachIntegration,
    updateEmailBisonIntegration,
    isUpdatingEmailBisonIntegration,
    connectRb2b,
    isConnectingRb2b,
  };
}
